import { useEffect, useRef, useState } from 'react';
import { TINYAI_CONSTANTS } from '../constants';
import { getDOMElements, getInitialViewportHeight } from '../utils/domElements';
import { isIOS } from '../utils/platformDetection';

/**
 * Detects mobile keyboard visibility by monitoring visual viewport changes.
 * Adjusts viewport height dynamically and notifies parent components of keyboard state.
 */
interface UseKeyboardDetectionOptions {
  enabled: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const useKeyboardDetection = ({
  enabled,
  inputRef,
  onVisibilityChange,
}: UseKeyboardDetectionOptions) => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [offsetTop, setOffsetTop] = useState(0);
  // Capture the pre-keyboard window height once; use it as the stable reference
  // for detection ratio so iOS quirks with window.innerHeight don't affect us.
  const initialWindowHeight = useRef(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );

  useEffect(() => {
    if (!enabled) return;

    const { body, root, rootContainer } = getDOMElements();
    // iOS Safari handles viewport resizing natively; manual body height
    // manipulation causes a "double adjustment" that pushes fixed elements off-screen.
    const iosDevice = isIOS();

    const updateViewportHeight = (
      vpHeight: number,
      isKeyboardOpen: boolean,
    ) => {
      setIsKeyboardVisible(isKeyboardOpen);
      setViewportHeight(isKeyboardOpen ? vpHeight : null);
      setOffsetTop(isKeyboardOpen ? (window.visualViewport?.offsetTop ?? 0) : 0);
      onVisibilityChange?.(isKeyboardOpen);

      if (!iosDevice) {
        body.style.height = `${vpHeight}px`;
        if (root) {
          root.style.height = `${vpHeight}px`;
        }
        if (rootContainer) {
          rootContainer.style.height = `${vpHeight}px`;
        }
      }
    };

    const handleResize = () => {
      if (window.visualViewport) {
        const vpHeight = window.visualViewport.height;
        const isKeyboardOpen =
          vpHeight <
          initialWindowHeight.current * TINYAI_CONSTANTS.KEYBOARD_DETECTION_RATIO;
        updateViewportHeight(vpHeight, isKeyboardOpen);
      }
    };

    const handleFocus = () => {
      setTimeout(() => {
        if (window.visualViewport) {
          const vpHeight = window.visualViewport.height;
          const isKeyboardOpen =
            vpHeight <
            initialWindowHeight.current *
              TINYAI_CONSTANTS.KEYBOARD_DETECTION_RATIO;
          updateViewportHeight(vpHeight, isKeyboardOpen);
        }
      }, TINYAI_CONSTANTS.KEYBOARD_FOCUS_DELAY);
    };

    const handleBlur = () => {
      setTimeout(() => {
        const initialHeight = getInitialViewportHeight();
        setIsKeyboardVisible(false);
        setViewportHeight(null);
        onVisibilityChange?.(false);

        if (!iosDevice) {
          body.style.height = `${initialHeight}px`;
          if (root) {
            root.style.height = `${initialHeight}px`;
          }
          if (rootContainer) {
            rootContainer.style.height = `${initialHeight}px`;
          }
        }
      }, TINYAI_CONSTANTS.KEYBOARD_BLUR_DELAY);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      // 'scroll' fires on iOS when the visual viewport shifts (offsetTop changes)
      // due to the keyboard — 'resize' alone may not catch this.
      window.visualViewport.addEventListener('scroll', handleResize);
    }

    const textarea = inputRef.current;
    if (textarea) {
      textarea.addEventListener('focus', handleFocus);
      textarea.addEventListener('blur', handleBlur);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
      if (textarea) {
        textarea.removeEventListener('focus', handleFocus);
        textarea.removeEventListener('blur', handleBlur);
      }
    };
  }, [enabled, inputRef, onVisibilityChange]);

  return { isKeyboardVisible, viewportHeight, offsetTop };
};
